/**
 * DATA TASKS PAGE
 *
 * Lists all data configuration tasks grouped by organization.
 * Data Team members can view and work on their assigned tasks.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { canAccessDataWork } from '@/lib/team-access';
import type { Location, Organization, Task } from '@/types/database';
import { ArrowRight, Building2, CheckCircle, Clock, Database, MapPin, Search, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface TaskWithDetails extends Task {
  organization: Organization;
  location: Location;
}

export default function DataTasksPage() {
  const { user, loading: authLoading } = useAuth();
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [deconfigTasks, setDeconfigTasks] = useState<TaskWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [mode, setMode] = useState<'activation' | 'deactivation'>('activation');

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      // Check role authorization
      if (!canAccessDataWork(user.role)) {
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch all IT setup tasks to check completion status
        const { data: itTasks, error: itTasksError } = await supabase
          .from('tasks')
          .select('id, location_id, status')
          .eq('type', 'IT_PROVISIONING');

        if (itTasksError) throw itTasksError;

        // Build a map of location_id to IT task completion status
        const completedItLocations = new Set(
          (itTasks || [])
            .filter((task) => task.status === 'COMPLETED')
            .map((task) => task.location_id)
        );

        // Fetch all data configuration tasks with organization and location details
        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select(`
            *,
            organization:organizations(id, name, status, logo_url),
            location:locations(id, name, description, logo_url, status, mapping_status)
          `)
          .eq('type', 'DATA_CONFIGURATION')
          .order('status', { ascending: true })
          .order('created_at', { ascending: false });

        if (tasksError) throw tasksError;

        // Filter data configuration tasks to only show those with completed IT tasks
        const filteredTasks = (tasksData as TaskWithDetails[]).filter(
          (task) => completedItLocations.has(task.location_id)
        ) || [];

        // Fetch all data deconfig tasks
        const { data: deconfigData, error: deconfigError } = await supabase
          .from('tasks')
          .select(`
            *,
            organization:organizations(id, name, status, logo_url),
            location:locations(id, name, description, logo_url, status, mapping_status)
          `)
          .eq('type', 'DATA_DECONFIG')
          .order('status', { ascending: true })
          .order('created_at', { ascending: false });

        if (deconfigError) throw deconfigError;

        setTasks(filteredTasks);
        setDeconfigTasks((deconfigData as TaskWithDetails[]) || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Error fetching data tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <Card className='border-red-200 dark:border-red-800'>
          <CardContent className='pt-6'>
            <p className='text-sm text-red-600 dark:text-red-400'>
              Error loading tasks: {error.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user || (user.role !== 'DATA_TEAM' && user.role !== 'DATA_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    return null;
  }

  return (
    <div className='container mx-auto px-4'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold mb-2'>Data Tasks</h1>
        <p className='text-muted-foreground'>
          Configure data pipelines for new locations and manage deactivations
        </p>
      </div>

      {/* Tab Navigation */}
      <div className='flex gap-2 mb-8 border-b border-gray-200 dark:border-gray-800'>
        <button
          onClick={() => setMode('activation')}
          className={`px-4 py-2 font-medium transition-colors ${
            mode === 'activation'
              ? 'border-b-2 border-green-600 text-green-600 dark:text-green-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          Activation
        </button>
        <button
          onClick={() => setMode('deactivation')}
          className={`px-4 py-2 font-medium transition-colors ${
            mode === 'deactivation'
              ? 'border-b-2 border-red-600 text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          Deactivation
        </button>
      </div>

      {mode === 'activation' && <ActivationContent tasks={tasks} />}
      {mode === 'deactivation' && <DeactivationContent deconfigTasks={deconfigTasks} />}
    </div>
  );
}

function ActivationContent({ tasks }: { tasks: TaskWithDetails[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterOrganization, setFilterOrganization] = useState<string | null>(null);

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter((task) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      task.organization?.name.toLowerCase().includes(searchLower) ||
      task.location?.name.toLowerCase().includes(searchLower) ||
      task.location?.description?.toLowerCase().includes(searchLower);

    const matchesStatus = !filterStatus || task.status === filterStatus;
    const matchesOrg = !filterOrganization || task.organization?.id === filterOrganization;

    return matchesSearch && matchesStatus && matchesOrg;
  });

  // Group tasks by status
  const pendingTasks = filteredTasks.filter((t) => t.status === 'PENDING');
  const completedTasks = filteredTasks.filter((t) => t.status === 'COMPLETED');

  // Get unique organizations for filter dropdown
  const uniqueOrganizations = Array.from(
    new Map(tasks.map((task) => [task.organization?.id, task.organization])).values()
  );

  return (
    <div className='space-y-6'>
      {/* Search and Filter Bar */}
      <div className='flex flex-col sm:flex-row gap-3'>
        <div className='flex-1 relative'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
          <Input
            type='text'
            placeholder='Search by organization or location...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='pl-10'
          />
        </div>

        <div className='flex gap-2'>
          <Select value={filterStatus || 'all'} onValueChange={(value) => setFilterStatus(value === 'all' ? null : value)}>
            <SelectTrigger className='w-40'>
              <SelectValue placeholder='All Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='PENDING'>Pending</SelectItem>
              <SelectItem value='COMPLETED'>Completed</SelectItem>
            </SelectContent>
          </Select>

          {uniqueOrganizations.length > 1 && (
            <Select value={filterOrganization || 'all'} onValueChange={(value) => setFilterOrganization(value === 'all' ? null : value)}>
              <SelectTrigger className='w-40'>
                <SelectValue placeholder='All Organizations' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Organizations</SelectItem>
                {uniqueOrganizations.map((org) => (
                  <SelectItem key={org?.id} value={org?.id || ''}>
                    {org?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {(searchTerm || filterStatus || filterOrganization) && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setSearchTerm('');
                setFilterStatus(null);
                setFilterOrganization(null);
              }}
              className='whitespace-nowrap'
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {tasks.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <Database className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>No activation tasks yet</h3>
            <p className='text-muted-foreground text-center max-w-md'>
              Data tasks will appear here once IT setup is complete.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Kanban Board */
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-320px)] min-h-[500px] max-h-[calc(100vh-320px)] overflow-hidden'>
          {/* Pending Column */}
          <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <Clock className='h-5 w-5 text-yellow-600 dark:text-yellow-400' />
                <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Pending</h2>
              </div>
              <span className='bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                {pendingTasks.length}
              </span>
            </div>
            <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
              {pendingTasks.map((task) => (
                <LocationCard key={task.id} task={task} />
              ))}
            </div>
          </div>

          {/* Completed Column */}
          <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <CheckCircle className='h-5 w-5 text-green-600 dark:text-green-400' />
                <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Completed</h2>
              </div>
              <span className='bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                {completedTasks.length}
              </span>
            </div>
            <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
              {completedTasks.map((task) => (
                <LocationCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeactivationContent({ deconfigTasks }: { deconfigTasks: TaskWithDetails[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterOrganization, setFilterOrganization] = useState<string | null>(null);

  // Filter tasks based on search and filters
  const filteredTasks = deconfigTasks.filter((task) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      task.organization?.name.toLowerCase().includes(searchLower) ||
      task.location?.name.toLowerCase().includes(searchLower) ||
      task.location?.description?.toLowerCase().includes(searchLower);

    const matchesStatus = !filterStatus || task.status === filterStatus;
    const matchesOrg = !filterOrganization || task.organization?.id === filterOrganization;

    return matchesSearch && matchesStatus && matchesOrg;
  });

  // Group deconfig tasks by status
  const pendingDeconfigTasks = filteredTasks.filter((t) => t.status === 'PENDING');
  const completedDeconfigTasks = filteredTasks.filter((t) => t.status === 'COMPLETED');

  // Get unique organizations for filter dropdown
  const uniqueOrganizations = Array.from(
    new Map(deconfigTasks.map((task) => [task.organization?.id, task.organization])).values()
  );

  return (
    <div className='space-y-6'>
      {/* Search and Filter Bar */}
      <div className='flex flex-col sm:flex-row gap-3'>
        <div className='flex-1 relative'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
          <Input
            type='text'
            placeholder='Search by organization or location...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='pl-10'
          />
        </div>

        <div className='flex gap-2'>
          <Select value={filterStatus || 'all'} onValueChange={(value) => setFilterStatus(value === 'all' ? null : value)}>
            <SelectTrigger className='w-40'>
              <SelectValue placeholder='All Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='PENDING'>Pending</SelectItem>
              <SelectItem value='COMPLETED'>Completed</SelectItem>
            </SelectContent>
          </Select>

          {uniqueOrganizations.length > 1 && (
            <Select value={filterOrganization || 'all'} onValueChange={(value) => setFilterOrganization(value === 'all' ? null : value)}>
              <SelectTrigger className='w-40'>
                <SelectValue placeholder='All Organizations' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Organizations</SelectItem>
                {uniqueOrganizations.map((org) => (
                  <SelectItem key={org?.id} value={org?.id || ''}>
                    {org?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {(searchTerm || filterStatus || filterOrganization) && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setSearchTerm('');
                setFilterStatus(null);
                setFilterOrganization(null);
              }}
              className='whitespace-nowrap'
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {deconfigTasks.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <Database className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>No deactivation tasks yet</h3>
            <p className='text-muted-foreground text-center max-w-md'>
              Deactivation tasks will appear here once locations are approved for deactivation.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Kanban Board */
        <div className='space-y-4'>
          {/* Warning Banner */}
          <div className='bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 p-4 rounded-md'>
            <p className='text-xs text-yellow-800 dark:text-yellow-200'>
              <strong>⚠️ Critical:</strong> Completing data deconfig tasks will trigger subscription cancellation. Ensure all data is archived.
            </p>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-380px)] min-h-[500px] max-h-[calc(100vh-380px)] overflow-hidden'>
            {/* Pending Column */}
            <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
              <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
                <div className='flex items-center gap-2'>
                  <XCircle className='h-5 w-5 text-red-600 dark:text-red-400' />
                  <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Pending</h2>
                </div>
                <span className='bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                  {pendingDeconfigTasks.length}
                </span>
              </div>
              <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
                {pendingDeconfigTasks.map((task) => (
                  <LocationCard key={task.id} task={task} isDeconfig />
                ))}
              </div>
            </div>

            {/* Completed Column */}
            <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
              <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
                <div className='flex items-center gap-2'>
                  <CheckCircle className='h-5 w-5 text-green-600 dark:text-green-400' />
                  <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Completed</h2>
                </div>
                <span className='bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                  {completedDeconfigTasks.length}
                </span>
              </div>
              <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
                {completedDeconfigTasks.map((task) => (
                  <LocationCard key={task.id} task={task} isDeconfig />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationCard({ task, isDeconfig = false, className }: { task: TaskWithDetails; isDeconfig?: boolean; className?: string }) {
  const isCompleted = task.status === 'COMPLETED';
  const detailPath = isDeconfig ? `/data-tasks/deconfig/${task.id}` : `/data-tasks/${task.id}`;

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-md border ${
        isCompleted
          ? 'border-green-200 dark:border-green-800 bg-white dark:bg-gray-800'
          : task.status === 'IN_PROGRESS'
          ? 'border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800'
          : isDeconfig
          ? 'border-red-200 dark:border-red-800 bg-white dark:bg-gray-800'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800'
      } ${className || ''}`}
    >
      <div className='p-4'>
        {/* Location Header */}
        <div className='flex items-start gap-3 mb-3'>
          {/* Location Logo - Smaller for Kanban */}
          {task.location?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                  <img
                    src={task.location.logo_url}
                    alt={`${task.location.name} logo`}
                    className='w-full h-full object-cover'
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                    isDeconfig
                      ? 'bg-red-100 dark:bg-red-900/20'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  {isDeconfig ? (
                    <XCircle className='w-6 h-6 text-red-600 dark:text-red-400' />
                  ) : (
                    <MapPin className='w-6 h-6 text-gray-400' />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Location Info */}
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 mb-1.5'>
              {isDeconfig && (
                <XCircle className='w-3.5 h-3.5 text-red-600 dark:text-red-400 shrink-0' />
              )}
              <h3 className='font-bold text-gray-900 dark:text-gray-100 truncate text-sm'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{task.location?.name}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">Location</TooltipContent>
                </Tooltip>
              </h3>
            </div>

            {/* Organization Badge */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className='inline-flex items-center gap-1 px-2 py-0.5 bg-black dark:bg-white text-white dark:text-black text-xs font-medium rounded-full'
                  title='Parent Organization'
                >
                  <Building2 className='w-3 h-3 shrink-0' />
                  <span className='truncate max-w-[150px]'>{task.organization?.name}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">Organization</TooltipContent>
            </Tooltip>

            {/* Additional Badges */}
            {isDeconfig && (
              <span className='inline-block px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium rounded-full mt-1.5'>
                DEACTIVATION
              </span>
            )}

            {task.location?.description && (
              <p className='text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2'>
                {task.location.description}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-800'>
          <div className='text-xs text-gray-600 dark:text-gray-500'>
            {isCompleted
              ? `✓ ${new Date(task.completed_at!).toLocaleDateString()}`
              : task.status === 'IN_PROGRESS'
              ? 'In progress...'
              : 'Ready'}
          </div>
          {!isCompleted && (
            <Button asChild size='sm' variant={isDeconfig ? 'destructive' : 'default'}>
              <Link to={detailPath}>
                {task.status === 'PENDING' ? 'Start' : 'Continue'}
                <ArrowRight className='ml-1 h-3 w-3' />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
